// Each entry defines one HR resource: its table columns (for the list view)
// and its form fields (for the add/edit modal). Adding a new module later is
// just adding an entry here plus a matching one in server/routes/hr.js —
// no new page code required.
//
// NOTE: "employees" is intentionally NOT here — it has its own dedicated
// pages (Employees.jsx / EmployeeForm.jsx / EmployeeProfile.jsx) since it
// needs conditional caregiver-specific tabs that this generic system can't
// express. See server/routes/employees.js.
//
// NOTE: "recruitment" is also intentionally NOT here anymore — it moved to
// its own dedicated module (Recruitment.jsx / ApplicantProfile.jsx /
// PublicApply.jsx) once the public application form + auto-hire flow
// shipped. See server/routes/recruitment.js.

export const HR_MODULES = {
  attendance: {
    label: "Attendance",
    pk: "id",
    columns: ["date", "employee_id", "clock_in", "clock_out", "hours_worked", "status"],
    fields: [
      { name: "employee_id", label: "Employee ID", type: "text", required: true },
      { name: "date", label: "Date", type: "date", required: true },
      { name: "clock_in", label: "Clock In", type: "time" },
      { name: "clock_out", label: "Clock Out", type: "time" },
      { name: "hours_worked", label: "Hours Worked", type: "number" },
      { name: "late_minutes", label: "Late (minutes)", type: "number" },
      { name: "overtime_hours", label: "Overtime Hours", type: "number" },
      { name: "status", label: "Status", type: "select", options: ["Present", "Absent", "Late", "Half-day", "On Leave"] },
      { name: "approved_by", label: "Approved By", type: "text" },
    ],
  },
  leave_requests: {
    label: "Leave Requests",
    pk: "id",
    columns: ["employee_id", "leave_type", "start_date", "end_date", "days", "status"],
    fields: [
      { name: "employee_id", label: "Employee ID", type: "text", required: true },
      { name: "leave_type", label: "Leave Type", type: "select", options: ["Annual", "Sick", "Maternity", "Paternity", "Unpaid", "Compassionate"] },
      { name: "start_date", label: "Start Date", type: "date" },
      { name: "end_date", label: "End Date", type: "date" },
      { name: "days", label: "Days", type: "number" },
      { name: "reason", label: "Reason", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["Pending", "Approved", "Rejected"] },
      { name: "approved_by", label: "Approved By", type: "text" },
      { name: "return_date", label: "Return Date", type: "date" },
    ],
  },
  payroll: {
    label: "Payroll",
    pk: "id",
    columns: ["employee_id", "month", "basic_salary", "commission", "net_salary", "payment_status"],
    fields: [
      { name: "employee_id", label: "Employee ID", type: "text", required: true },
      { name: "month", label: "Month (e.g. 2026-07)", type: "text" },
      { name: "basic_salary", label: "Basic Salary", type: "number" },
      { name: "commission", label: "Commission", type: "number" },
      { name: "bonus", label: "Bonus", type: "number" },
      { name: "allowances", label: "Allowances", type: "number" },
      { name: "ssnit", label: "SSNIT Deduction", type: "number" },
      { name: "paye", label: "PAYE Tax", type: "number" },
      { name: "other_deductions", label: "Other Deductions", type: "number" },
      { name: "net_salary", label: "Net Salary", type: "number" },
      { name: "payment_date", label: "Payment Date", type: "date" },
      { name: "payment_status", label: "Payment Status", type: "select", options: ["Pending", "Paid"] },
    ],
  },
  performance_reviews: {
    label: "Performance Reviews",
    pk: "id",
    columns: ["employee_id", "review_period", "policies_sold", "premium_generated", "overall_score"],
    fields: [
      { name: "employee_id", label: "Employee ID", type: "text", required: true },
      { name: "review_period", label: "Review Period (e.g. Q2 2026)", type: "text" },
      { name: "policies_sold", label: "Policies Sold", type: "number" },
      { name: "premium_generated", label: "Premium Generated", type: "number" },
      { name: "renewal_rate", label: "Renewal Rate (0-1)", type: "number" },
      { name: "customer_rating", label: "Customer Rating (1-5)", type: "number" },
      { name: "attendance_score", label: "Attendance Score (1-5)", type: "number" },
      { name: "teamwork_score", label: "Teamwork Score (1-5)", type: "number" },
      { name: "initiative_score", label: "Initiative Score (1-5)", type: "number" },
      { name: "overall_score", label: "Overall Score (1-5)", type: "number" },
      { name: "manager_comments", label: "Manager Comments", type: "textarea" },
    ],
  },
  training: {
    label: "Training",
    pk: "id",
    columns: ["employee_id", "course", "trainer", "training_date", "score"],
    fields: [
      { name: "employee_id", label: "Employee ID", type: "text", required: true },
      { name: "course", label: "Course", type: "text", required: true },
      { name: "trainer", label: "Trainer", type: "text" },
      { name: "training_date", label: "Training Date", type: "date" },
      { name: "score", label: "Score", type: "number" },
      { name: "certificate", label: "Certificate Issued", type: "select", options: [{ value: 1, label: "Yes" }, { value: 0, label: "No" }] },
      { name: "next_training", label: "Next Training Due", type: "date" },
    ],
  },
  assets: {
    label: "Assets",
    pk: "asset_id",
    columns: ["asset_id", "asset_name", "category", "assigned_to", "condition", "status"],
    fields: [
      { name: "asset_id", label: "Asset ID", placeholder: "Leave blank to auto-generate", type: "text" },
      { name: "asset_name", label: "Asset Name", type: "text", required: true },
      { name: "category", label: "Category", type: "text" },
      { name: "brand", label: "Brand", type: "text" },
      { name: "serial_number", label: "Serial Number", type: "text" },
      { name: "purchase_date", label: "Purchase Date", type: "date" },
      { name: "purchase_price", label: "Purchase Price", type: "number" },
      { name: "assigned_to", label: "Assigned To (Employee ID)", type: "text" },
      { name: "condition", label: "Condition", type: "select", options: ["New", "Good", "Fair", "Poor", "Damaged"] },
      { name: "location", label: "Location", type: "text" },
      { name: "status", label: "Status", type: "select", options: ["In Use", "In Storage", "Under Repair", "Disposed"] },
    ],
  },
  inventory: {
    label: "Inventory",
    pk: "id",
    columns: ["item_name", "category", "quantity", "minimum_stock", "supplier"],
    fields: [
      { name: "item_name", label: "Item Name", type: "text", required: true },
      { name: "category", label: "Category", type: "text" },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "minimum_stock", label: "Minimum Stock", type: "number" },
      { name: "supplier", label: "Supplier", type: "text" },
      { name: "purchase_date", label: "Purchase Date", type: "date" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
  },
  staff_loans: {
    label: "Staff Loans",
    pk: "id",
    columns: ["employee_id", "loan_amount", "issue_date", "monthly_deduction", "balance", "status"],
    fields: [
      { name: "employee_id", label: "Employee ID", type: "text", required: true },
      { name: "loan_amount", label: "Loan Amount", type: "number" },
      { name: "issue_date", label: "Issue Date", type: "date" },
      { name: "monthly_deduction", label: "Monthly Deduction", type: "number" },
      { name: "balance", label: "Balance Remaining", type: "number" },
      { name: "status", label: "Status", type: "select", options: ["Active", "Paid Off", "Defaulted"] },
    ],
  },
  disciplinary_cases: {
    label: "Disciplinary Cases",
    pk: "id",
    columns: ["employee_id", "incident_date", "offence", "warning_level", "resolved"],
    fields: [
      { name: "employee_id", label: "Employee ID", type: "text", required: true },
      { name: "incident_date", label: "Incident Date", type: "date" },
      { name: "offence", label: "Offence", type: "textarea" },
      { name: "action_taken", label: "Action Taken", type: "textarea" },
      { name: "warning_level", label: "Warning Level", type: "select", options: ["Verbal", "Written", "Final", "Termination"] },
      { name: "resolved", label: "Resolved", type: "select", options: [{ value: 1, label: "Yes" }, { value: 0, label: "No" }] },
    ],
  },
  tasks: {
    label: "Tasks",
    pk: "id",
    columns: ["task_name", "assigned_to", "priority", "due_date", "status"],
    fields: [
      { name: "task_name", label: "Task Name", type: "text", required: true },
      { name: "category", label: "Category", type: "text" },
      { name: "assigned_to", label: "Assigned To (Employee ID)", type: "text" },
      { name: "priority", label: "Priority", type: "select", options: ["Low", "Medium", "High", "Urgent"] },
      { name: "due_date", label: "Due Date", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["Not Started", "In Progress", "Completed", "Overdue"] },
      { name: "completed_date", label: "Completed Date", type: "date" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
};

export const HR_MODULE_ORDER = [
  "attendance", "leave_requests", "payroll", "performance_reviews",
  "training", "assets", "inventory", "staff_loans",
  "disciplinary_cases", "tasks",
];
